import React, { useState, useEffect } from 'react';
import { Plus, Target, Edit, Trash2, TrendingUp, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_BASE}/api`;

const GoalTracker = ({ onStatsUpdate }) => {
  const [goals, setGoals] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [loading, setLoading] = useState(true);

  const goalCategories = [
    { value: 'fitness', label: 'FITNESS', icon: '💪', color: 'text-neon-green' },
    { value: 'mindset', label: 'MINDSET', icon: '🧠', color: 'text-electric-blue' },
    { value: 'dating', label: 'DATING', icon: '❤️', color: 'text-pink-400' },
    { value: 'productivity', label: 'PRODUCTIVITY', icon: '🚀', color: 'text-yellow-400' }
  ];

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to load goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async (goalData) => {
    try {
      await axios.post(`${API}/goals`, goalData);
      await loadGoals();
      onStatsUpdate?.();
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create goal:', error);
    }
  };

  const updateGoal = async (goalId, goalData) => {
    try {
      await axios.put(`${API}/goals/${goalId}`, goalData);
      await loadGoals();
      onStatsUpdate?.();
      setEditingGoal(null);
    } catch (error) {
      console.error('Failed to update goal:', error);
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      await loadGoals();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Failed to delete goal:', error);
    }
  };

  const updateProgress = async (goalId, progressMade) => {
    try {
      await axios.post(`${API}/goals/${goalId}/progress`, { progress_made: progressMade });
      await loadGoals();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getCategoryInfo = (category) => {
    return goalCategories.find(cat => cat.value === category) || goalCategories[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-primary rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-white">Loading your goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="heading-bold text-white flex items-center">
          <Target className="w-7 h-7 mr-3 text-neon-green" />
          YOUR GOALS
        </h2>
        
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn-power flex items-center space-x-2 px-6 py-3"
        >
          <Plus className="w-5 h-5" />
          <span>ADD GOAL</span>
        </button>
      </div>

      {/* Goals Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.map((goal) => {
          const categoryInfo = getCategoryInfo(goal.category);
          const progressPercent = getProgressPercentage(goal.current_value, goal.target_value);
          const isCompleted = goal.status === 'completed';
          
          return (
            <div
              key={goal.id}
              className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 border transition-all hover:scale-105 ${
                isCompleted ? 'border-neon-green/50 bg-neon-green/5' : 'border-gray-700 hover:border-electric-blue/50'
              }`}
            >
              {/* Goal Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{categoryInfo.icon}</span>
                  <span className={`text-xs font-bold uppercase tracking-wide ${categoryInfo.color}`}>
                    {categoryInfo.label}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {isCompleted && <CheckCircle className="w-5 h-5 text-neon-green" />}
                  
                  <button
                    onClick={() => setEditingGoal(goal)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => deleteGoal(goal.id)}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Goal Content */}
              <h3 className="text-white font-bold text-lg mb-2">{goal.title}</h3>
              <p className="text-gray-300 text-sm mb-4 line-clamp-2">{goal.description}</p>

              {/* Progress */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Progress</span>
                  <span className="text-white font-bold">
                    {goal.current_value} / {goal.target_value} {goal.unit}
                  </span>
                </div>
                
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${
                      isCompleted ? 'bg-neon-green' : 'bg-gradient-primary'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  ></div>
                </div>
                
                <div className="text-center">
                  <span className={`text-lg font-bold ${
                    isCompleted ? 'text-neon-green' : 'text-white'
                  }`}>
                    {progressPercent.toFixed(0)}% Complete
                  </span>
                </div>

                {/* Quick Progress Update */}
                {!isCompleted && (
                  <div className="flex items-center space-x-2 pt-2">
                    <button
                      onClick={() => updateProgress(goal.id, 1)}
                      className="flex-1 bg-electric-blue/20 hover:bg-electric-blue/30 text-electric-blue font-bold py-2 px-3 rounded-lg text-sm transition-colors"
                    >
                      +1 {goal.unit}
                    </button>
                    <button
                      onClick={() => {
                        const progress = prompt(`Add progress for "${goal.title}" (${goal.unit}):`);
                        if (progress && !isNaN(progress) && Number(progress) > 0) {
                          updateProgress(goal.id, Number(progress));
                        }
                      }}
                      className="bg-neon-green/20 hover:bg-neon-green/30 text-neon-green font-bold py-2 px-4 rounded-lg text-sm transition-colors"
                    >
                      <TrendingUp className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {goals.length === 0 && (
          <div className="col-span-full bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 border border-gray-700 text-center">
            <Target className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-white font-bold text-xl mb-2">NO GOALS YET</h3>
            <p className="text-gray-400 mb-6">
              Ready to CRUSH some goals? Let's set your first target and start your transformation!
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-power px-8 py-3"
            >
              CREATE YOUR FIRST GOAL
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Goal Modal */}
      {(showCreateForm || editingGoal) && (
        <GoalForm
          goal={editingGoal}
          categories={goalCategories}
          onSubmit={editingGoal ? (data) => updateGoal(editingGoal.id, data) : createGoal}
          onCancel={() => {
            setShowCreateForm(false);
            setEditingGoal(null);
          }}
        />
      )}
    </div>
  );
};

// Goal Form Component
const GoalForm = ({ goal, categories, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: goal?.title || '',
    description: goal?.description || '',
    category: goal?.category || 'fitness',
    target_value: goal?.target_value || '',
    unit: goal?.unit || 'workouts'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.target_value) return;
    
    onSubmit({
      ...formData,
      target_value: Number(formData.target_value)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-50">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 max-w-md w-full border border-electric-blue/30">
        <h3 className="heading-bold text-white mb-6">
          {goal ? 'EDIT GOAL' : 'CREATE NEW GOAL'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Selection */}
          <div className="grid grid-cols-2 gap-3">
            {categories.map((category) => (
              <button
                key={category.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, category: category.value }))}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.category === category.value
                    ? 'border-electric-blue bg-electric-blue/20'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-2xl mb-1">{category.icon}</div>
                <div className={`text-xs font-bold ${category.color}`}>{category.label}</div>
              </button>
            ))}
          </div>

          {/* Goal Title */}
          <input
            type="text"
            placeholder="Goal title (e.g., 'Workout 5x per week')"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-600 focus:border-electric-blue outline-none"
            required
          />

          {/* Description */}
          <textarea
            placeholder="Describe your goal..."
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-600 focus:border-electric-blue outline-none resize-none"
            rows="3"
          />

          {/* Target & Unit */}
          <div className="flex space-x-3">
            <input
              type="number"
              placeholder="Target"
              value={formData.target_value}
              onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value }))}
              className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-600 focus:border-electric-blue outline-none"
              required
              min="1"
            />
            <input
              type="text"
              placeholder="Unit (e.g., workouts, pounds)"
              value={formData.unit}
              onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
              className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-xl border border-gray-600 focus:border-electric-blue outline-none"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-3 rounded-xl font-bold transition-colors"
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="flex-1 btn-power py-3"
            >
              {goal ? 'UPDATE' : 'CREATE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalTracker;